// hooks
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

// utils
import {
  differenceInCalendarDays,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay
} from 'date-fns';
import {
  parseBackendDateTime,
  suffixZ,
  toBackendDateTime,
  toDateString
} from 'utils/date';
// types
import type {
  AbsenceRolloutDutyLine,
  RegisterAbsenceWorkAgreement
} from 'RestAPIs/roster/register-absence';
import {
  createAbsenceUpdateDuties,
  getAbsenceById,
  getRegisterAbsenceDuties,
  updateAbsence
} from 'RestAPIs/roster/register-absence';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import showError from 'components/shared/showError';
import { byWorkAgreement, getBenefitChanges } from './benefitChanges';
import { firstFreeDay } from './overlapPeriods';
import { createDefaultSetting } from './workAgreementSetting';
import { useAbsenceRollout } from './useAbsenceRollout';
import { useDutyHourTypes } from './useDutyHourTypes';
import { useOverlapDetection } from './useOverlapDetection';
import { useWorkingDaysSettings } from './useWorkingDaysSettings';
import type { RegisterAbsenceDialogEmployee } from './RegisterAbsenceDialog.types';
import type { DateChangeOrigin } from 'components/shared/DatePicker/DatePicker.types';
import type { WorkAgreementSetting } from './workAgreementSetting';

// The aanvraagreden is always one of two codes and follows directly from the
// chosen duty hour type: ziekte → ZK, ziekte t.g.v. zwangerschap → ZW
const REASON_CODE_BY_DUTY_HOUR_TYPE_CODE: Record<string, string> = {
  ZK: 'ZK',
  ZKZ: 'ZW'
};

/** the duties endpoint returns one row per benefit, so a work agreement whose
 *  inschaling changes inside the period comes back more than once */
const firstRowPerWorkAgreement = (rows: RegisterAbsenceWorkAgreement[]) => {
  const firstByWaId = new Map<number, RegisterAbsenceWorkAgreement>();
  for (const row of rows) {
    if (!firstByWaId.has(row.waId)) {
      firstByWaId.set(row.waId, row);
    }
  }
  return [...firstByWaId.values()];
};

/**
 * Edit mode does not call the rollout: the consequences follow from the linked
 * duties and the backend's own rules, computed here. A duty that falls
 * completely outside the shrunk period is deleted; the remaining duties convert
 * when the duty hour type changed and stay as they are otherwise.
 */
const toEditDutyLines = ({
  duties,
  originalTypeDescription,
  startDate,
  endDate,
  typeChanged
}: {
  duties: AbsenceRolloutDutyLine[];
  originalTypeDescription: string;
  startDate: Date | null;
  endDate: Date | null;
  typeChanged: boolean;
}) => {
  // every linked line now carries the absence's duty hour type, whatever
  // planned duty it came from: while editing the row is named by that type
  // ("Ziekte · ongewijzigd"), not by the code of the original duty ("E06").
  // When the type is unknown the label stands alone ("ongewijzigd"): the
  // planned duty code would name a duty that is no longer there
  const withType = duties.map((line) => ({
    ...line,
    displayDutyCode: originalTypeDescription,
    dutyCode: undefined
  }));
  if (!startDate || !endDate) {
    return withType;
  }
  const periodStart = startOfDay(startDate);
  const periodEnd = endOfDay(endDate);

  return withType.map((line) => {
    const outsidePeriod =
      isBefore(parseBackendDateTime(line.endTime), periodStart) ||
      isAfter(parseBackendDateTime(line.startTime), periodEnd);
    return {
      ...line,
      action: outsidePeriod ? 'DELETE' : typeChanged ? 'MODIFY' : 'SKIP'
    };
  });
};

type UseRegisterAbsenceDialogArgs = {
  open: boolean;
  /** absent until the planner picks one in the dialog itself (a new notice
   *  from the overview); the form stays idle until then */
  employee?: RegisterAbsenceDialogEmployee;
  /** Prefilled period, e.g. from the absence notice being processed */
  initialStartDate?: Date;
  initialEndDate?: Date;
  /** editing an existing absence: loads it and switches the dialog to edit
   *  mode (period can only shrink, no rollout, PUT on save) */
  absenceId?: number;
  onClose: () => void;
  onSuccess?: () => void;
};

export const useRegisterAbsenceDialog = ({
  open: isDialogOpen,
  employee,
  initialStartDate,
  initialEndDate,
  absenceId,
  onClose,
  onSuccess
}: UseRegisterAbsenceDialogArgs) => {
  // NOTE: deliberately no use-memo directive here, so the useMemo calls below
  // stay. The React Compiler skips this hook entirely (`npm run
  // react-compiler:check` reports InvalidReact) because of the deliberate
  // react-hooks/exhaustive-deps suppressions that remain after the #2399 split
  // (the reset, seeding and follow-the-period effects), so annotating it would
  // drop the memoization instead of taking it over.
  const isEdit = absenceId !== undefined;
  // without an employee nothing can be loaded or saved yet: every query and
  // the reset treat the form as closed until the employee is picked
  const open = isDialogOpen && employee !== undefined;
  const employeeId = employee?.id ?? 0;
  const today = useMemo(() => startOfDay(new Date()), []);
  const [startDate, setStartDate] = useState<Date | null>(
    initialStartDate ?? today
  );
  const [endDate, setEndDate] = useState<Date | null>(
    initialEndDate ?? initialStartDate ?? today
  );
  const [percentage, setPercentage] = useState(100);
  const [note, setNote] = useState('');
  const [selectedWaIds, setSelectedWaIds] = useState<number[]>([]);
  const [workAgreementSettings, setWorkAgreementSettings] = useState<
    Record<number, WorkAgreementSetting>
  >({});

  const seenWaIdsRef = useRef<Set<number>>(new Set());
  const { getSettings, saveSettings } = useWorkingDaysSettings();

  // reset the form every time the dialog opens for a (new) employee
  useEffect(() => {
    if (open) {
      setStartDate(initialStartDate ?? today);
      setEndDate(initialEndDate ?? initialStartDate ?? today);
      setNote('');
      setPercentage(100);
      setSelectedWaIds([]);
      setWorkAgreementSettings({});
      seenWaIdsRef.current = new Set();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employeeId, absenceId]);

  // edit mode: the absence record, its work agreement and its linked duties
  // in one request
  const absenceDetailsQuery = useQuery(
    ['registerAbsenceDetails', absenceId],
    async () => (await getAbsenceById(absenceId!)).data,
    { enabled: open && isEdit }
  );
  const originalAbsence = absenceDetailsQuery.data?.absence;
  const originalStartDate = useMemo(
    () =>
      originalAbsence
        ? startOfDay(parseBackendDateTime(originalAbsence.startTime))
        : null,
    [originalAbsence]
  );
  const originalEndDate = useMemo(
    () =>
      originalAbsence
        ? startOfDay(parseBackendDateTime(originalAbsence.endTime))
        : null,
    [originalAbsence]
  );

  // Moving one date past the other drags the other along to the same day:
  // a single-day absence, instead of an end-before-start error.
  //
  // A typed date is the exception. It arrives one keystroke at a time and
  // every keystroke leaves a complete date behind, so typing "14" as the last
  // day passes through the 1st of the month. Dragging on that intermediate
  // value moves the start date to the 1st and nothing moves it back: the
  // planner types one date and silently keeps a longer absence than intended.
  // So a typed value that would invert the period is left alone until the
  // rest of it arrives — or until the field is left, which reports the same
  // date again without the typing mark and drags as before.
  const changeStartDate = (date: Date | null, origin?: DateChangeOrigin) => {
    const invertsPeriod =
      !!date && !!endDate && differenceInCalendarDays(endDate, date) < 0;
    if (invertsPeriod && origin?.typing) {
      return;
    }
    setStartDate(date);
    if (invertsPeriod) {
      setEndDate(date);
    }
  };
  const changeEndDate = (date: Date | null, origin?: DateChangeOrigin) => {
    const invertsPeriod =
      !!date && !!startDate && differenceInCalendarDays(startDate, date) > 0;
    if (invertsPeriod && origin?.typing) {
      return;
    }
    setEndDate(date);
    if (invertsPeriod) {
      setStartDate(date);
    }
  };

  // Clearing a date field does not clear the period: the picker keeps the last
  // value it accepted, so the dialog always has a date to save. Measured on
  // 13 aug 2026 — see [V27].
  const hasValidPeriod = !!startDate && !!endDate && endDate >= startDate;
  const endBeforeStart = !!startDate && !!endDate && endDate < startDate;
  // in calendar days, so a DST switch inside the period cannot swallow a day
  const dayCount =
    startDate && endDate && !endBeforeStart
      ? differenceInCalendarDays(endDate, startDate) + 1
      : 0;

  const { dutyHourTypes, hasDutyHourTypesResult, dutyTypeId, setDutyTypeId } =
    useDutyHourTypes({
      open,
      isEdit,
      hasValidPeriod,
      startDate,
      endDate,
      today
    });

  // populate the form from the loaded absence (once per open)
  useEffect(() => {
    if (!open || !isEdit || !originalAbsence) {
      return;
    }
    setStartDate(startOfDay(parseBackendDateTime(originalAbsence.startTime)));
    setEndDate(startOfDay(parseBackendDateTime(originalAbsence.endTime)));
    setDutyTypeId(originalAbsence.dutyTypeId);
    setPercentage(originalAbsence.absPercentage ?? 100);
    setNote(originalAbsence.note ?? '');
  }, [open, isEdit, originalAbsence, setDutyTypeId]);

  const workAgreementsQuery = useQuery(
    [
      'registerAbsenceWorkAgreements',
      employeeId,
      toDateString(startDate ?? today),
      toDateString(endDate ?? startDate ?? today)
    ],
    async () =>
      (
        await getRegisterAbsenceDuties({
          employeeId,
          startDate: toBackendDateTime(startDate ?? today),
          endDate: toBackendDateTime(endDate ?? startDate ?? today, true)
        })
      ).data.workAgreements,
    // edit mode brings its own work agreement along with the absence
    { enabled: open && hasValidPeriod && !isEdit, keepPreviousData: true }
  );

  const workAgreementRows = useMemo(
    () => workAgreementsQuery.data ?? [],
    [workAgreementsQuery.data]
  );

  // edit mode: the absence's own work agreement, as loaded with the absence
  // in number order (0, 1, 2), whatever order the endpoint returns them in:
  // the tabs show the real number, and the consequences panel lists the own
  // plan units in this same order
  const workAgreements = useMemo(
    () =>
      [
        ...(isEdit
          ? (absenceDetailsQuery.data?.workAgreements ?? []).filter(Boolean)
          : firstRowPerWorkAgreement(workAgreementRows))
      ].sort((a, b) => a.nr - b.nr),
    [isEdit, absenceDetailsQuery.data, workAgreementRows]
  );

  // every work agreement starts SELECTED, so all planned duties show right
  // away; unchecking one marks its duties as untouched in the consequences.
  // an explicit uncheck survives refetches (only unseen ids are seeded).
  // the rollout settings are seeded per work agreement with their defaults.
  useEffect(() => {
    setSelectedWaIds((current) => {
      const next = current.filter((id) =>
        workAgreements.some((workAgreement) => workAgreement.waId === id)
      );
      for (const workAgreement of workAgreements) {
        if (!seenWaIdsRef.current.has(workAgreement.waId)) {
          seenWaIdsRef.current.add(workAgreement.waId);
          next.push(workAgreement.waId);
        }
      }
      return next;
    });
    setWorkAgreementSettings((current) => {
      const next = { ...current };
      const recorded = getSettings();
      for (const workAgreement of workAgreements) {
        next[workAgreement.waId] =
          next[workAgreement.waId] ??
          createDefaultSetting(
            workAgreement,
            startDate,
            recorded[workAgreement.waId]
          );
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workAgreements]);

  const benefitChanges = useMemo(
    () => getBenefitChanges(workAgreementRows, startDate, endDate),
    [workAgreementRows, startDate, endDate]
  );
  const benefitChangeByWaId = useMemo(
    () => byWorkAgreement(benefitChanges),
    [benefitChanges]
  );
  // said at the period, for the checked work agreements: that is where the
  // planner can still act on it. Not while editing: the period may only
  // shrink there, and a cached register answer for the same days must not
  // bring the notice (a disabled query still returns cached data)
  const periodBenefitChanges = isEdit
    ? []
    : benefitChanges.filter((change) => selectedWaIds.includes(change.waId));
  /** run the absence up to the last day of the current inschaling */
  const endAtBenefitEnd = (waId: number) => {
    const lastDay = benefitChanges.find((change) => change.waId === waId)
      ?.lastDay;
    if (lastDay) {
      changeEndDate(lastDay);
    }
  };

  const updateWorkAgreementSetting = (
    waId: number,
    patch: Partial<WorkAgreementSetting>
  ) =>
    setWorkAgreementSettings((current) => ({
      ...current,
      [waId]: {
        ...current[waId],
        ...patch,
        // a hand-picked rollout date stops following the absence period
        ...('rolloutStartDate' in patch
          ? { rolloutStartDateTouched: true }
          : {}),
        // an explicit (un)check stops the automatic opt-in below
        ...('useFteRollout' in patch ? { useFteRolloutTouched: true } : {})
      }
    }));

  const editDutyLines = useMemo(
    () =>
      isEdit
        ? toEditDutyLines({
            duties: absenceDetailsQuery.data?.duties ?? [],
            originalTypeDescription:
              dutyHourTypes.find(
                (type) => type.dutyTypeId === originalAbsence?.dutyTypeId
              )?.description ?? '',
            startDate,
            endDate,
            typeChanged:
              !!originalAbsence &&
              typeof dutyTypeId === 'number' &&
              dutyTypeId !== originalAbsence.dutyTypeId
          })
        : [],
    [
      isEdit,
      absenceDetailsQuery.data,
      dutyHourTypes,
      startDate,
      endDate,
      dutyTypeId,
      originalAbsence
    ]
  );

  const {
    existingDutyLines,
    newDutyLines,
    selectedNewDutyLines,
    dutyCountByWaId,
    rolloutErrorDutyLines,
    rolloutOverMaxHoursPerDay,
    isRolloutLoading,
    hasRolloutResult,
    rolloutErrorKey
  } = useAbsenceRollout({
    open,
    isEdit,
    hasValidPeriod,
    employeeId,
    dutyTypeId,
    startDate,
    endDate,
    percentage,
    workAgreements,
    selectedWaIds,
    workAgreementSettings,
    setWorkAgreementSettings,
    editDutyLines
  });

  const {
    overlappingAbsences,
    hasBlockingOverlap,
    nearbyAbsencePeriods,
    coveredAbsencePeriods,
    constrainingPeriods,
    isRegisteredAbsenceDay
  } = useOverlapDetection({
    open,
    isEdit,
    absenceId,
    employeeId,
    startDate,
    endDate,
    hasValidPeriod,
    today,
    workAgreements,
    selectedWaIds
  });

  /**
   * The rollout starts on the first day of the absence, so moving the period
   * moves it along — unless the planner picked a date themselves.
   *
   * A day another absence already covers is skipped: the period may overlap
   * (that is what keeps the overlap visible), but the day sickness duties start
   * being created may not, so the field would open on a value it refuses.
   */
  useEffect(() => {
    if (!startDate) {
      return;
    }
    const from = firstFreeDay(startDate, constrainingPeriods);
    setWorkAgreementSettings((current) =>
      Object.fromEntries(
        Object.entries(current).map(([waId, setting]) => {
          if (!setting.rolloutStartDateTouched) {
            return [waId, { ...setting, rolloutStartDate: from }];
          }
          // A date the planner picked themselves still has to sit inside the
          // period: duties are only created there, so a date before the start
          // or past the last day quietly rolls out nothing. Moving the period
          // away from it pulls it back in rather than leaving a value that
          // cannot do anything.
          const picked = setting.rolloutStartDate;
          const outside =
            !!picked &&
            (isBefore(picked, startOfDay(startDate)) ||
              (!!endDate && isAfter(picked, startOfDay(endDate))));

          return [
            waId,
            outside ? { ...setting, rolloutStartDate: from } : setting
          ];
        })
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, nearbyAbsencePeriods, selectedWaIds]);

  const queryClient = useQueryClient();
  const saveMutation = useMutation(createAbsenceUpdateDuties, {
    onSuccess: () => {
      showSuccessSnackbar();
      onClose();
      queryClient.invalidateQueries('getAbsenceNotices');
      onSuccess?.();
    },
    onError: showError
  });
  const updateMutation = useMutation(updateAbsence, {
    onSuccess: () => {
      showSuccessSnackbar();
      onClose();
      queryClient.invalidateQueries('getAbsenceNotices');
      // the type change may partially fail per duty (backend logs and moves
      // on), so the next open must show the real state, not this cache
      queryClient.invalidateQueries(['registerAbsenceDetails', absenceId]);
      onSuccess?.();
    },
    onError: showError
  });

  // the backend only allows shrinking the period
  const startBeforeOriginal =
    isEdit &&
    !!startDate &&
    !!originalStartDate &&
    differenceInCalendarDays(startDate, originalStartDate) < 0;
  const endAfterOriginal =
    isEdit &&
    !!endDate &&
    !!originalEndDate &&
    differenceInCalendarDays(endDate, originalEndDate) > 0;

  /** do the working days differ from what was recorded for this work agreement? */
  const isWorkingDaysChanged = (waId: number) => {
    const setting = workAgreementSettings[waId];
    if (!setting) {
      return false;
    }
    const stored = getSettings()[waId]?.days;
    if (!stored) {
      return setting.inputDays.length > 0;
    }
    return [...stored].sort().join() !== [...setting.inputDays].sort().join();
  };

  /** store the current working days as the setting for next time */
  const saveWorkingDaysSetting = (waIds: number[]) =>
    saveSettings(
      Object.fromEntries(
        waIds.map((waId) => [
          waId,
          { days: workAgreementSettings[waId]?.inputDays ?? [] }
        ])
      )
    );

  /** every blocker except the reason and an in-flight save */
  const readyExceptReason =
    hasValidPeriod &&
    typeof dutyTypeId === 'number' &&
    (isEdit || selectedWaIds.length > 0) &&
    !hasBlockingOverlap &&
    !startBeforeOriginal &&
    !endAfterOriginal &&
    !(isEdit && !originalAbsence);

  const canSave =
    employee !== undefined &&
    readyExceptReason &&
    note.trim().length > 0 &&
    !saveMutation.isLoading &&
    !updateMutation.isLoading;

  /** the missing reason is the last thing standing between the planner and
   *  Save — the reason field names itself then, without waiting for a blur */
  const reasonIsOnlyBlocker = readyExceptReason && note.trim().length === 0;

  /** nothing changes in the planning: no duty of a selected work agreement is
   *  actually touched (SKIP means the backend leaves it as it is) and no new
   *  duties are created */
  const hasNoConsequences =
    !existingDutyLines.some(
      (line) =>
        !!line.waId &&
        selectedWaIds.includes(line.waId) &&
        line.action !== 'SKIP'
    ) && selectedNewDutyLines.length === 0;

  const save = () => {
    if (!canSave || !startDate || !endDate || typeof dutyTypeId !== 'number') {
      return;
    }
    const dutyHourType = dutyHourTypes.find(
      (type) => type.dutyTypeId === dutyTypeId
    );

    if (isEdit && originalAbsence) {
      // an unchanged date keeps the absence's original time of day: the
      // backend refuses anything outside the original window, and dojo-era
      // absences do not necessarily run 00:00-23:59
      const sameStartDay = isSameDay(
        startDate,
        parseBackendDateTime(originalAbsence.startTime)
      );
      const sameEndDay = isSameDay(
        endDate,
        parseBackendDateTime(originalAbsence.endTime)
      );
      updateMutation.mutate({
        absenceId: originalAbsence.absenceId,
        workAgreementId: originalAbsence.waId,
        employeeId,
        startTime: sameStartDay
          ? originalAbsence.startTime
          : toBackendDateTime(startDate),
        endTime: sameEndDay
          ? originalAbsence.endTime
          : toBackendDateTime(endDate, true),
        note,
        dutyTypeId,
        // derived like on create; null keeps the existing reason code
        absenceReasonCode:
          REASON_CODE_BY_DUTY_HOUR_TYPE_CODE[dutyHourType?.code ?? ''] ?? null
      });
      return;
    }

    saveMutation.mutate({
      startTime: toBackendDateTime(startDate),
      endTime: toBackendDateTime(endDate, true),
      dutyTypeId,
      dccSvc: dutyHourType?.dccSvc ?? 0,
      note,
      // derived, not asked: the aanvraagreden follows from the duty hour type;
      // the note carries the human-readable reason for the overview
      absenceReasonCode:
        REASON_CODE_BY_DUTY_HOUR_TYPE_CODE[dutyHourType?.code ?? ''] ?? null,
      // not created from a leave request; 0 would violate the FK constraint
      leaveRequestId: null,
      employeeId,
      workAgreements: workAgreements
        .filter((workAgreement) => selectedWaIds.includes(workAgreement.waId))
        .map((workAgreement) => {
          const setting =
            workAgreementSettings[workAgreement.waId] ??
            createDefaultSetting(workAgreement, startDate);
          return {
            waId: workAgreement.waId,
            benefitId: workAgreement.benefitId,
            hoursWeek: setting.inputWeekHours,
            absPercentage: percentage,
            dutiesToUpdate: existingDutyLines
              .filter(
                (line) =>
                  line.action === 'MODIFY' &&
                  line.id &&
                  line.waId === workAgreement.waId
              )
              .map((line) => ({
                dutyLineId: line.id!,
                dutyDatetimeBegin: suffixZ(line.startTime),
                dutyDatetimeEnd: suffixZ(line.endTime)
              })),
            dutiesToDelete: existingDutyLines
              .filter(
                (line) =>
                  line.action === 'DELETE' &&
                  line.id &&
                  line.waId === workAgreement.waId
              )
              .map((line) => ({
                dutyLineId: line.id!,
                dutyDatetimeBegin: suffixZ(line.startTime),
                dutyDatetimeEnd: suffixZ(line.endTime)
              })),
            dutiesToCreate: newDutyLines
              .filter((line) => line.waId === workAgreement.waId)
              .map((line) => ({
                dutyDatetimeBegin: suffixZ(line.startTime),
                dutyDatetimeEnd: suffixZ(line.endTime)
              }))
          };
        })
    });
  };

  return {
    startDate,
    setStartDate: changeStartDate,
    endDate,
    setEndDate: changeEndDate,
    endBeforeStart,
    dayCount,
    dutyTypeId,
    setDutyTypeId,
    dutyHourTypes,
    hasDutyHourTypesResult,
    percentage,
    setPercentage,
    note,
    setNote,
    workAgreements,
    selectedWaIds,
    setSelectedWaIds,
    workAgreementSettings,
    updateWorkAgreementSetting,
    dutyCountByWaId,
    benefitChangeByWaId,
    periodBenefitChanges,
    endAtBenefitEnd,
    existingDutyLines,
    selectedNewDutyLines,
    rolloutErrorDutyLines,
    rolloutOverMaxHoursPerDay,
    isRolloutLoading,
    // the panel may only judge "nothing happens" on an actual result, never
    // on the emptiness before the first calculation arrived
    hasConsequencesResult: isEdit
      ? !!absenceDetailsQuery.data
      : hasRolloutResult,
    rolloutErrorKey,
    hasNoConsequences,
    overlappingAbsences,
    hasBlockingOverlap,
    coveredAbsencePeriods,
    isRegisteredAbsenceDay,
    // whether detaching a planned duty gives its request back as an open duty
    detachedGivesOpenDuty: !!dutyHourTypes
      .find((type) => type.dutyTypeId === dutyTypeId)
      ?.flags?.includes('REQ_DISCONNECT_AUTO'),
    isWorkingDaysChanged,
    saveWorkingDaysSetting,
    canSave,
    reasonIsOnlyBlocker,
    isSaving: saveMutation.isLoading || updateMutation.isLoading,
    save,
    // edit mode
    isEdit,
    isLoadingAbsence: isEdit && absenceDetailsQuery.isLoading,
    originalAbsence,
    originalStartDate,
    originalEndDate,
    startBeforeOriginal,
    endAfterOriginal
  };
};
